import React, { Suspense, lazy, useEffect, useState, useMemo } from "react";
import "./App.css";
import { ThemeContext } from "./Contexts/ThemeContext";
import { RouterProvider, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import router from "./Routes/Routes";
import useProduct from "./Hooks/useProduct";
import useCart from "./Hooks/useCart";
import useOrder from "./Hooks/useOrder";
import useScroll from "./Hooks/useScroll";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "./firebase.init";
import ReactGA from "react-ga4";

// ReactGA.initialize("G-3G0Q3PYC1F");

// Lazy load Product component
const Product = lazy(() => import("./Components/Shared/Product"));

// 🔥 Separate component for GA route tracking
const AnalyticsTracker = () => {
  const location = useLocation();

  // useEffect(() => {
  //   ReactGA.send({
  //     hitType: "pageview",
  //     page: location.pathname + location.search,
  //   });
  // }, [location]);

  return null;
};

function App() {
  const [user] = useAuthState(auth);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [products, loadingProducts] = useProduct();
  const [cart, setCart] = useCart(products);
  const [orderList, setOrderList] = useOrder();
  const [scrollPosition] = useScroll();
  const [searchText, setSearchText] = useState("");
  const [favProducts, setFavProducts] = useState([]);
  const [summerMakeup, setSummerMakeup] = useState([]);
  const [nudeMakeup, setNudeMakeup] = useState([]);
  const [searchedProducts, setSearchedProducts] = useState([]);
  const [allOrder, setAllOrder] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);

  // 🔥 Traffic Source Tracking Function
  const setCustomerOrigin = () => {
    const params = new URLSearchParams(window.location.search);
    const referrer = document.referrer ? document.referrer.toLowerCase() : "";

    let source = "direct";

    const utmSource = (params.get("utm_source") || "").toLowerCase();
    const utmMedium = (params.get("utm_medium") || "").toLowerCase();

    // 1. UTM
    if (utmSource) {
      if (["cpc", "ppc", "paid"].includes(utmMedium)) {
        source = "paid";
      } else if (
        [
          "facebook",
          "instagram",
          "tiktok",
          "linkedin",
          "twitter",
          "whatsapp",
        ].includes(utmSource)
      ) {
        source = "social_media";
      } else {
        source = "organic";
      }
    }

    // 2. Click IDs
    else if (
      params.get("gclid") ||
      params.get("fbclid") ||
      params.get("ttclid")
    ) {
      source = "paid";
    }

    // 3. Referrer
    else if (referrer) {
      if (
        referrer.includes("facebook.com") ||
        referrer.includes("m.facebook.com") ||
        referrer.includes("instagram.com") ||
        referrer.includes("tiktok.com") ||
        referrer.includes("linkedin.com") ||
        referrer.includes("twitter.com") ||
        referrer.includes("t.co") ||
        referrer.includes("whatsapp.com") ||
        referrer.includes("web.whatsapp.com")
      ) {
        source = "social_media";
      } else if (
        referrer.includes("google.") ||
        referrer.includes("bing.") ||
        referrer.includes("yahoo.")
      ) {
        source = "organic";
      } else {
        source = "referral";
      }
    }

    // 🔥 First-touch save (important)
    if (!localStorage.getItem("customer_from")) {
      localStorage.setItem("customer_from", source);
    }

    // Extra data
    if (!localStorage.getItem("landing_page")) {
      localStorage.setItem("landing_page", window.location.href);
    }

    if (!localStorage.getItem("first_referrer")) {
      localStorage.setItem("first_referrer", referrer || "direct");
    }

    // GA Event (only once per session)
    if (!sessionStorage.getItem("tracked")) {
      ReactGA.event({
        category: "Traffic",
        action: "Source",
        label: source,
      });
      sessionStorage.setItem("tracked", "true");
    }

    console.log("Traffic Source:", source);
  };

  // 🔥 Run tracking once safely
  useEffect(() => {
    if (!sessionStorage.getItem("origin_tracked")) {
      setCustomerOrigin();
      sessionStorage.setItem("origin_tracked", "true");
    }
  }, []);

  useEffect(() => {
    // First page load
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });

    // Listen route changes (pushState / popstate)
    const trackPageView = () => {
      ReactGA.send({
        hitType: "pageview",
        page: window.location.pathname + window.location.search,
      });
    };

    // Override pushState
    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args) {
      originalPushState.apply(this, args);
      trackPageView();
    };

    // Listen back/forward
    window.addEventListener("popstate", trackPageView);

    return () => {
      window.history.pushState = originalPushState;
      window.removeEventListener("popstate", trackPageView);
    };
  }, []);

  useEffect(() => {
    // Fetch admin status
    const fetchAdminStatus = async () => {
      setIsAdminLoading(true);
      try {
        let isAdminFromPhone = false;
        let isAdminFromEmail = false;

        if (user?.phoneNumber) {
          const response = await fetch(
            `https://ebay-backend.vercel.app/users/admin/${user.phoneNumber}`,
          );
          const data = await response.json();
          isAdminFromPhone = data.isAdmin;
        }

        if (user?.email) {
          const response = await fetch(
            `https://ebay-backend.vercel.app/users/admin1/${user.email}`,
          );
          const data = await response.json();
          isAdminFromEmail = data.isAdmin;
        }

        setIsAdmin(isAdminFromPhone || isAdminFromEmail);
      } catch (error) {
        console.error("Error fetching admin status:", error);
      } finally {
        setIsAdminLoading(false);
      }
    };

    fetchAdminStatus();
  }, [user]);

  useEffect(() => {
    // Fetch initial data
    const fetchInitialData = async () => {
      try {
        const [favProductsRes, ordersRes, blogsRes] = await Promise.all([
          fetch(
            "https://ebay-backend.vercel.app/getProductsByCategories?name=face wash",
          ).then((res) => res.json()),
          fetch("https://ebay-backend.vercel.app/orders").then((res) =>
            res.json(),
          ),
          fetch("https://ebay-backend.vercel.app/getBlogs").then((res) =>
            res.json(),
          ),
        ]);

        setFavProducts(favProductsRes);
        setAllOrder(ordersRes);
        setBlogs(blogsRes);
        setBlogLoading(false); // Assuming blogs are loaded after fetch
      } catch (error) {
        console.error("Error fetching initial data:", error);
        setBlogLoading(false); // Handle loading state in case of error
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    // Search functionality
    if (searchText.length > 2) {
      const result = products?.filter((product) =>
        product.name.toLowerCase().includes(searchText.toLowerCase()),
      );
      setSearchedProducts(result);
    }
  }, [searchText, products]);

  const goToTop = () => {
    window.scrollTo(0, 0);
  };

  const contextValue = useMemo(
    () => ({
      products,
      blogs,
      blogLoading,
      allOrder,
      loading: loadingProducts,
      favProducts,
      summerMakeup,
      nudeMakeup,
      searchedProducts,
      cart,
      setCart,
      orderList,
      setOrderList,
      searchText,
      setSearchText,
      appliedCoupon,
      setAppliedCoupon,
      user,
      isAdmin,
      isAdminLoading,
    }),
    [
      products,
      blogs,
      blogLoading,
      allOrder,
      loadingProducts,
      favProducts,
      summerMakeup,
      nudeMakeup,
      searchedProducts,
      cart,
      setCart,
      orderList,
      setOrderList,
      searchText,
      appliedCoupon,
      user,
      isAdmin,
      isAdminLoading,
    ],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <RouterProvider router={router}></RouterProvider>
      <a
        href="https://api.whatsapp.com/send?phone=8801404403965"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg
          title="Chat on WhatsApp"
          className={`transition-all cursor-pointer w-10 h-10 duration-300 ease-in-out fixed bottom-[50px] right-8 ${
            scrollPosition >= 0
              ? "opacity-0 lg:opacity-100"
              : "opacity-0 pointer-events-none"
          }`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
        >
          <path
            fill="#25D366"
            d="M16 .7C7.6.7.7 7.6.7 16c0 2.7.7 5.2 2 7.5L0 32l8.7-2.7c2.3 1.3 4.9 2 7.5 2 8.4 0 15.3-6.9 15.3-15.3S24.4.7 16 .7zm0 27.9c-2.4 0-4.8-.6-6.9-1.9l-.5-.3-5.1 1.6 1.7-5-.3-.5c-1.3-2.1-1.9-4.5-1.9-6.9 0-7.2 5.8-13 13-13s13 5.8 13 13-5.9 13-13 13zm7.4-9.7c-.4-.2-2.3-1.1-2.6-1.2-.3-.1-.5-.2-.7.2-.2.4-.8 1.2-1 1.4-.2.2-.4.3-.8.1-.4-.2-1.6-.6-3-1.9-1.1-1-1.9-2.3-2.1-2.7-.2-.4 0-.6.2-.8.2-.2.4-.4.5-.6.2-.2.3-.4.5-.6.2-.2.1-.5 0-.7-.1-.2-.7-1.8-1-2.5-.3-.7-.6-.6-.8-.6h-.7c-.2 0-.7.1-1 .5s-1.3 1.2-1.3 3 1.3 3.4 1.5 3.6c.2.2 2.6 3.9 6.4 5.4.9.4 1.6.7 2.1.9.9.3 1.7.3 2.4.2.7-.1 2.3-1 2.6-1.9.3-.9.3-1.7.2-1.9-.1-.2-.3-.3-.7-.5z"
          />
        </svg>
      </a>

      <Toaster />
    </ThemeContext.Provider>
  );
}

export default App;

// https://ebay-backend.vercel.app
